import React, { useEffect, useMemo, useRef, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";

const MODULE_LABELS = { dms: "PLM", manufacturing: "Manufacturing" };
const KIND_LABELS = { admin: "Admin", alert_recipient: "Alert recipient" };

const badge = (bg, color) => ({
  background: bg,
  color,
  borderRadius: 10,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
});

// A checkbox that can show "some of these" — the tenant row when only part of
// its people are picked.
function TriStateCheckbox({ checked, indeterminate, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} aria-label={label} />;
}

/**
 * Everyone a broadcast to `audience` would reach, grouped by tenant, with a
 * checkbox per tenant and per person. Everyone starts ticked whenever the
 * audience changes. Reports the ticked user ids through onSelectionChange.
 */
export default function BroadcastRecipients({ audience, onSelectionChange, onError }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    superadminAPI
      .getBroadcastRecipients(audience)
      .then((resp) => {
        if (cancelled) return;
        const groups = resp.data?.tenants || [];
        setTenants(groups);
        setSelected(new Set(groups.flatMap((t) => t.recipients.map((r) => r.userId))));
        setCollapsed(new Set());
      })
      .catch((err) => {
        if (!cancelled) onError(err.response?.data?.message || "Failed to load recipients");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  const everyone = useMemo(() => tenants.flatMap((t) => t.recipients), [tenants]);

  // The same address can sit under two tenants; it is still mailed once.
  const uniqueSelectedEmails = useMemo(() => {
    const emails = new Set();
    for (const r of everyone) if (selected.has(r.userId)) emails.add(r.email.toLowerCase());
    return emails.size;
  }, [everyone, selected]);

  useEffect(() => {
    onSelectionChange({ userIds: [...selected], uniqueEmails: uniqueSelectedEmails });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, uniqueSelectedEmails]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!q) return tenants;
    return tenants
      .map((t) =>
        t.name.toLowerCase().includes(q)
          ? t
          : { ...t, recipients: t.recipients.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q)) },
      )
      .filter((t) => t.recipients.length);
  }, [tenants, q]);

  const setMany = (userIds, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      userIds.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const toggleCollapsed = (tenantId) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });

  // Select all / Clear act on what the search shows, so "search, then tick"
  // works without unticking everything else.
  const visibleIds = visible.flatMap((t) => t.recipients.map((r) => r.userId));

  return (
    <div style={{ marginBottom: 14, maxWidth: 760 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#0f2d5c" }}>
          Recipients{" "}
          <span style={{ fontWeight: 400, color: "#475569" }}>
            {loading
              ? "— loading…"
              : `— ${selected.size} of ${everyone.length} selected · ${uniqueSelectedEmails} unique email${uniqueSelectedEmails === 1 ? "" : "s"}`}
          </span>
        </span>
        <input
          type="search"
          placeholder="Filter by tenant, name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            marginLeft: "auto",
            padding: "4px 8px",
            border: "1px solid #cbd5e1",
            borderRadius: 4,
            fontSize: 12,
            minWidth: 200,
          }}
        />
        <button type="button" className="superadmin-link-btn" onClick={() => setMany(visibleIds, true)}>
          Select all
        </button>
        <button type="button" className="superadmin-link-btn" onClick={() => setMany(visibleIds, false)}>
          Clear
        </button>
      </div>

      <div
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          background: "#fff",
          maxHeight: 360,
          overflowY: "auto",
        }}
      >
        {!loading && visible.length === 0 && (
          <p style={{ margin: 0, padding: 16, fontSize: 13, color: "#94a3b8" }}>
            {q ? "Nobody matches that filter." : "No tenant admins in this audience."}
          </p>
        )}
        {visible.map((t) => {
          const ids = t.recipients.map((r) => r.userId);
          const picked = ids.filter((id) => selected.has(id)).length;
          const isCollapsed = collapsed.has(t.id);
          return (
            <div key={t.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  background: "#f8fafc",
                  position: "sticky",
                  top: 0,
                }}
              >
                <TriStateCheckbox
                  checked={picked === ids.length && ids.length > 0}
                  indeterminate={picked > 0 && picked < ids.length}
                  onChange={() => setMany(ids, picked < ids.length)}
                  label={`Select everyone at ${t.name}`}
                />
                <button
                  type="button"
                  onClick={() => toggleCollapsed(t.id)}
                  aria-expanded={!isCollapsed}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flex: 1,
                    textAlign: "left",
                    fontSize: 13,
                    color: "#0f2d5c",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ color: "#94a3b8", width: 10 }}>{isCollapsed ? "▸" : "▾"}</span>
                  {t.name}
                  <span style={badge(t.module === "manufacturing" ? "#fef3c7" : "#e0f2fe", t.module === "manufacturing" ? "#92400e" : "#075985")}>
                    {MODULE_LABELS[t.module] || t.module}
                  </span>
                </button>
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  {picked} of {ids.length}
                </span>
              </div>
              {!isCollapsed &&
                t.recipients.map((r) => (
                  <label
                    key={r.userId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 12px 6px 34px",
                      fontSize: 13,
                      cursor: "pointer",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(r.userId)}
                      onChange={(e) => setMany([r.userId], e.target.checked)}
                    />
                    <span style={{ color: "#0f172a", minWidth: 140 }}>{r.name}</span>
                    <span style={{ color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.email}
                    </span>
                    <span style={r.kind === "admin" ? badge("#e0e7ff", "#3730a3") : badge("#dcfce7", "#166534")}>
                      {KIND_LABELS[r.kind] || r.kind}
                    </span>
                  </label>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
